import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ThemeProvider, useAppTheme } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { canAccessFamily } from "./utils/familyAccess";
import MaintenancePage from "./pages/MaintenancePage";
import {
  fetchMaintenanceStatus,
  getMaintenanceState,
  installMaintenanceInterceptor,
  subscribeMaintenance,
} from "./utils/maintenanceAxios";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AttivaStruttura from "./pages/AttivaStruttura";
import Register from "./pages/Register";
import RegisterTrial from "./pages/RegisterTrial";
import RichiestaDemo from "./pages/RichiestaDemo";
import TrialExpired from "./pages/TrialExpired";
import TrialUpgrade from "./pages/TrialUpgrade";
import BillingSuccess from "./pages/BillingSuccess";
import RegisterBonificoSuccess from "./pages/RegisterBonificoSuccess";
import BillingCancel from "./pages/BillingCancel";
import Dashboard from "./pages/Dashboard";
import AlloggiatiWeb from "./pages/AlloggiatiWeb";
import PostiLiberi from "./pages/PostiLiberi";
import OspitiInRete from "./pages/OspitiInRete";
import Immobiliare from "./pages/Immobiliare";
import ImmobiliPortalLogin from "./pages/ImmobiliPortalLogin";
import ImmobiliPortal from "./pages/ImmobiliPortal";
import { ImmobiliPortalAuthProvider } from "./context/ImmobiliPortalAuthContext";
import Personale from "./pages/Personale";
import Risparmio from "./pages/Risparmio";
import ProfiloStruttura from "./pages/ProfiloStruttura";
import FascicoliAssistenziali from "./pages/FascicoliAssistenziali";
import FascicoloAssistenzialeDettaglio from "./pages/FascicoloAssistenzialeDettaglio";
import Newsletter from "./pages/Newsletter";
import Fatturazione from "./pages/Fatturazione";
import NuovaFattura from "./pages/NuovaFattura";
import ModelliFattura from "./pages/ModelliFattura";
import AspettoFattura from "./pages/AspettoFattura";
import Documenti from "./pages/Documenti";
import DiarioConsegne from "./pages/DiarioConsegne";
import DiarioPersonale from "./pages/DiarioPersonale";
import Impostazioni from "./pages/Impostazioni";
import Assistenza from "./pages/Assistenza";
import FamilyStruttura from "./pages/FamilyStruttura";
import NovitaAggiornamenti from "./pages/NovitaAggiornamenti";
import PrimaNotaOspiti from "./pages/PrimaNotaOspiti";
import Manutenzioni from "./pages/Manutenzioni";
import Deblistering from "./pages/Deblistering";
import FarmaciaLogin from "./pages/FarmaciaLogin";
import FamilyLogin from "./pages/FamilyLogin";
import FamilyLayout from "./components/FamilyLayout";
import { FamilyAuthProvider } from "./context/FamilyAuthContext";
import FamilySetupPassword from "./pages/family/FamilySetupPassword";
import FamilyForgotPassword from "./pages/family/FamilyForgotPassword";
import FamilyResetPassword from "./pages/family/FamilyResetPassword";
import FamilyRichieste from "./pages/family/FamilyRichieste";
import FamilyAmministrazione from "./pages/family/FamilyAmministrazione";
import {
  FamilyHome, FamilyPercorso, FamilyFarmaci, FamilyDocumenti, FamilyMomenti,
  FamilyAggiornamenti, FamilyProfilo, FamilyAltro, FamilyNotifiche,
} from "./pages/family/FamilyPages";
import FarmaciaDashboard from "./pages/FarmaciaDashboard";
import RichiesteCarepy from "./pages/farmacia/RichiesteCarepy";
import ControlloRicette from "./pages/farmacia/ControlloRicette";
import ClientiDiretti from "./pages/farmacia/ClientiDiretti";
import ImpostazioniFarmacia from "./pages/farmacia/ImpostazioniFarmacia";
import CatalogoFarmacia from "./pages/farmacia/CatalogoFarmacia";
import PanoramicaStruttura from "./pages/farmacia/PanoramicaStruttura";
import FarmaciaStrutture from "./pages/farmacia/FarmaciaStrutture";
import FarmaciaOspiti from "./pages/farmacia/FarmaciaOspiti";
import FarmaciaSchedaPaziente from "./pages/farmacia/FarmaciaSchedaPaziente";
import FarmaciaPratiche from "./pages/farmacia/FarmaciaPratiche";
import FarmaciaMagazzino from "./pages/farmacia/FarmaciaMagazzino";
import FarmaciaOrdini from "./pages/farmacia/FarmaciaOrdini";
import FarmaciaAmministrazione from "./pages/farmacia/FarmaciaAmministrazione";
import FarmaciaReport from "./pages/farmacia/FarmaciaReport";
import ScorteFarmacia from "./pages/farmacia/ScorteFarmacia";
import ConsegneFarmacia from "./pages/farmacia/ConsegneFarmacia";
import AutistaConsegneFarmacia from "./pages/farmacia/AutistaConsegneFarmacia";
import FatturazioneFarmacia from "./pages/farmacia/FatturazioneFarmacia";
import { FarmaciaRoot, default as FarmaciaLayout } from "./components/FarmaciaLayout";
import Arredo from "./pages/Arredo";
import TrovaOspiti from "./pages/TrovaOspiti";
import TrovaPersonale from "./pages/TrovaPersonale";
import Comunicazioni from "./pages/Comunicazioni";
import FirmaContratto from "./pages/FirmaContratto";
import FirmaDelegaMedico from "./pages/FirmaDelegaMedico";
import PreIngressoPortal from "./pages/preingresso/PreIngressoPortal";
import TerapiaLinkPortal from "./pages/terapia/TerapiaLinkPortal";
import DocumentiFirmaPortal from "./pages/documenti/DocumentiFirmaPortal";
import DelegaFirmaPortal from "./pages/deblistering/DelegaFirmaPortal";
import CaricaDocumentiPortal from "./pages/ospiti-rete/CaricaDocumentiPortal";
import AdminPanel from "./pages/admin/AdminPanel";
import SuperAdminLogin from "./pages/admin/SuperAdminLogin";
import AdminDemo from "./pages/admin/AdminDemo";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import TermsOfService from "./pages/TermsOfService";
import OssLogin from "./pages/OssLogin";
import OssLayout from "./components/OssLayout";
import { OssAuthProvider } from "./context/OssAuthContext";
import OssPwaShell from "./components/oss/OssPwaShell";
import OssFascicoli from "./pages/oss/OssFascicoli";
import OssFascicoloDettaglio from "./pages/oss/OssFascicoloDettaglio";
import OssCamere from "./pages/oss/OssCamere";
import OssCameraDettaglio from "./pages/oss/OssCameraDettaglio";
import OssTurni from "./pages/oss/OssTurni";
import OssPrimaNota from "./pages/oss/OssPrimaNota";
import OssDiarioConsegne from "./pages/oss/OssDiarioConsegne";
import OssDocumenti from "./pages/oss/OssDocumenti";
import OssDashboard from "./pages/oss/OssDashboard";
import OssManutenzioni from "./pages/oss/OssManutenzioni";
import OssRisparmio from "./pages/oss/OssRisparmio";
import OssMedici from "./pages/oss/OssMedici";
import OssStampeMensili from "./pages/oss/OssStampeMensili";
import OssManutentori from "./pages/oss/OssManutentori";
import OssOrdiniFarmacia from "./pages/oss/OssOrdiniFarmacia";
import SanitarioLogin from "./pages/SanitarioLogin";
import SanitarioLayout from "./components/SanitarioLayout";
import { SanitarioAuthProvider } from "./context/SanitarioAuthContext";
import SanitarioDashboard from "./pages/sanitario/SanitarioDashboard";
import SanitarioScheda from "./pages/sanitario/SanitarioScheda";
import SanitarioTerapie from "./pages/sanitario/SanitarioTerapie";
import SanitarioComunicazioni from "./pages/sanitario/SanitarioComunicazioni";
import SanitarioDeblistering from "./pages/sanitario/SanitarioDeblistering";
import SanitarioImpostazioni from "./pages/sanitario/SanitarioImpostazioni";
import CentroDiurnoDashboard from "./pages/centro-diurno/CentroDiurnoDashboard";
import CentroDiurnoAgenda from "./pages/centro-diurno/CentroDiurnoAgenda";
import CentroDiurnoPresenze from "./pages/centro-diurno/CentroDiurnoPresenze";
import CentroDiurnoTrasporti from "./pages/centro-diurno/CentroDiurnoTrasporti";
import CentroDiurnoMezzi from "./pages/centro-diurno/CentroDiurnoMezzi";
import CentroDiurnoReport from "./pages/centro-diurno/CentroDiurnoReport";
import CentroDiurnoPacchetti from "./pages/centro-diurno/CentroDiurnoPacchetti";
import AutistaLogin from "./pages/autista/AutistaLogin";
import AutistaLayout from "./components/AutistaLayout";
import AutistaGiro from "./pages/autista/AutistaGiro";
import { AutistaAuthProvider } from "./context/AutistaAuthContext";
import Layout from "./components/Layout";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import DriveShell from "./components/drive/DriveShell";
import VisiteShell from "./components/visite/VisiteShell";
import VisiteLogin from "./pages/visite/VisiteLogin";
import VisiteHome from "./pages/visite/VisiteHome";
import SanitarioShell from "./components/sanitario/SanitarioShell";
import "./App.css";

installMaintenanceInterceptor();

// Wrapper che fornisce OssAuthContext sia alla login che all'area OSS
function OssRoot() {
  return (
    <OssAuthProvider>
      <OssPwaShell>
        <Outlet />
      </OssPwaShell>
    </OssAuthProvider>
  );
}

function SanitarioRoot() {
  return (
    <SanitarioAuthProvider>
      <SanitarioShell />
    </SanitarioAuthProvider>
  );
}

function AutistaRoot() {
  return (
    <AutistaAuthProvider>
      <DriveShell />
    </AutistaAuthProvider>
  );
}

function VisiteRoot() {
  return <VisiteShell />;
}

function ImmobiliPortalRoot() {
  return (
    <ImmobiliPortalAuthProvider>
      <Outlet />
    </ImmobiliPortalAuthProvider>
  );
}

function FamilyRoot() {
  return (
    <FamilyAuthProvider>
      <Outlet />
    </FamilyAuthProvider>
  );
}

// Helper per redirect con parametri dinamici
const RedirectWithParams = ({ to }) => {
  const params = useParams();
  const newPath = Object.entries(params).reduce(
    (path, [key, value]) => path.replace(`:${key}`, value),
    to
  );
  return <Navigate to={newPath} replace />;
};

const ProtectedRoute = ({ children, requiredPermission, allowTrialExpired = false, requireAccess }) => {
  const { isAuthenticated, loading, hasPermission, user } = useAuth();
  const toastShownRef = useRef(false);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4CAF50]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.account_status === "trial_expired" && !allowTrialExpired) {
    return <Navigate to="/trial-expired" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    if (!toastShownRef.current) {
      toastShownRef.current = true;
      setTimeout(() => {
        toast.error("Accesso negato: non hai i permessi per visualizzare questa pagina.");
      }, 0);
    }
    return <Navigate to="/" replace />;
  }

  if (requireAccess && !requireAccess(user)) {
    if (!toastShownRef.current) {
      toastShownRef.current = true;
      setTimeout(() => {
        toast.error("Accesso negato: questa funzione non è abilitata per il tuo utente.");
      }, 0);
    }
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();
  const [maintenance, setMaintenance] = useState(getMaintenanceState());

  useEffect(() => subscribeMaintenance(setMaintenance), []);

  useEffect(() => {
    fetchMaintenanceStatus();
  }, []);

  const isAdminPath =
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/") ||
    location.pathname === "/admin-login" ||
    location.pathname.startsWith("/admin-login/") ||
    location.pathname === "/visite" ||
    location.pathname.startsWith("/visite/") ||
    location.pathname === "/visite-login";

  if (maintenance.enabled && !isAdminPath) {
    return <MaintenancePage />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register/trial" element={<RegisterTrial />} />
      <Route path="/richiedi-demo" element={<RichiestaDemo />} />
      <Route path="/register/bonifico-success" element={<RegisterBonificoSuccess />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <TrialUpgrade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trial-expired"
        element={
          <ProtectedRoute allowTrialExpired>
            <TrialExpired />
          </ProtectedRoute>
        }
      />
      {/* Legal Pages - Public */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/legal/privacy" element={<Navigate to="/privacy" replace />} />
      <Route path="/legal/cookies" element={<Navigate to="/cookies" replace />} />
      <Route path="/legal/terms" element={<Navigate to="/terms" replace />} />
      <Route path="/termini-di-servizio" element={<Navigate to="/terms" replace />} />
      {/* Portale familiare pre-ingresso — pubblico, token-based */}
      <Route path="/preingresso/:token" element={<PreIngressoPortal />} />
      <Route path="/terapia/:token" element={<TerapiaLinkPortal />} />
      <Route path="/delega-farmacia/:token" element={<DelegaFirmaPortal />} />
      <Route path="/documenti-firma/:token" element={<DocumentiFirmaPortal />} />
      <Route path="/upload/:token" element={<CaricaDocumentiPortal />} />
      <Route element={<FamilyRoot />}>
        <Route path="/family-login" element={<FamilyLogin />} />
        <Route path="/family/setup/:token" element={<FamilySetupPassword />} />
        <Route path="/family/forgot-password" element={<FamilyForgotPassword />} />
        <Route path="/family/reset-password" element={<FamilyResetPassword />} />
        <Route path="/family" element={<FamilyLayout />}>
          <Route index element={<FamilyHome />} />
          <Route path="percorso" element={<FamilyPercorso />} />
          <Route path="farmaci" element={<FamilyFarmaci />} />
          <Route path="documenti" element={<FamilyDocumenti />} />
          <Route path="momenti" element={<FamilyMomenti />} />
          <Route path="aggiornamenti" element={<FamilyAggiornamenti />} />
          <Route path="richieste" element={<FamilyRichieste />} />
          <Route path="richieste/:richiestaId" element={<FamilyRichieste />} />
          <Route path="amministrazione" element={<FamilyAmministrazione />} />
          <Route path="amministrazione/:fatturaId" element={<FamilyAmministrazione />} />
          <Route path="profilo" element={<FamilyProfilo />} />
          <Route path="altro" element={<FamilyAltro />} />
          <Route path="notifiche" element={<FamilyNotifiche />} />
        </Route>
      </Route>
      {/* Super Admin Login - Public */}
      <Route path="/admin-login" element={<SuperAdminLogin />} />
      {/* OSS Login e area riservata — OssAuthProvider condiviso per entrambi i sotto-alberi */}
      <Route element={<OssRoot />}>
        <Route path="/oss-login" element={<OssLogin />} />
        <Route path="/oss" element={<OssLayout />}>
          <Route index element={<OssDashboard />} />
          <Route path="fascicoli" element={<OssFascicoli />} />
          <Route path="fascicoli/:id" element={<OssFascicoloDettaglio />} />
          <Route path="camere" element={<OssCamere />} />
          <Route path="camere/:id" element={<OssCameraDettaglio />} />
          <Route path="turni" element={<OssTurni />} />
          <Route path="prima-nota" element={<OssPrimaNota />} />
          <Route path="diario-consegne" element={<OssDiarioConsegne />} />
          <Route path="documenti" element={<OssDocumenti />} />
          <Route path="manutenzioni" element={<OssManutenzioni />} />
          <Route path="risparmio" element={<OssRisparmio />} />
          <Route path="ordini-farmacia" element={<OssOrdiniFarmacia />} />
          <Route path="medici" element={<OssMedici />} />
          <Route path="manutentori" element={<OssManutentori />} />
          <Route path="stampe-mensili" element={<OssStampeMensili />} />
        </Route>
      </Route>
      {/* Modulo Sanitario Rapido — tablet OSS/infermieri, senza gestionale admin */}
      <Route element={<SanitarioRoot />}>
        <Route path="/sanitario-login" element={<SanitarioLogin />} />
        <Route path="/sanitario" element={<SanitarioLayout />}>
          <Route index element={<SanitarioDashboard />} />
          <Route path="scheda" element={<SanitarioScheda />} />
          <Route path="scheda/:id" element={<SanitarioScheda />} />
          <Route path="terapie" element={<SanitarioTerapie />} />
          <Route path="comunicazioni" element={<SanitarioComunicazioni />} />
          <Route path="comunicazioni/:canale" element={<SanitarioComunicazioni />} />
          <Route path="deblistering" element={<SanitarioDeblistering />} />
          <Route path="impostazioni" element={<SanitarioImpostazioni />} />
        </Route>
      </Route>
      <Route element={<AutistaRoot />}>
        <Route path="/autista-login" element={<AutistaLogin />} />
        <Route path="/autista" element={<AutistaLayout />}>
          <Route index element={<AutistaGiro />} />
        </Route>
      </Route>
      <Route element={<VisiteRoot />}>
        <Route path="/visite-login" element={<VisiteLogin />} />
        <Route path="/visite" element={<VisiteHome />} />
      </Route>
      <Route element={<FarmaciaRoot />}>
        <Route path="/farmacia-login" element={<FarmaciaLogin />} />
        <Route path="/attiva-struttura" element={<AttivaStruttura />} />
        <Route path="/farmacia" element={<FarmaciaLayout />}>
          <Route index element={<FarmaciaDashboard />} />
          <Route path="strutture" element={<FarmaciaStrutture />} />
          <Route path="ospiti" element={<FarmaciaOspiti />} />
          <Route path="ospite/:id" element={<FarmaciaSchedaPaziente />} />
          <Route path="pratiche" element={<FarmaciaPratiche />} />
          <Route path="magazzino" element={<FarmaciaMagazzino />} />
          <Route path="ordini" element={<FarmaciaOrdini />} />
          <Route path="amministrazione" element={<FarmaciaAmministrazione />} />
          <Route path="report" element={<FarmaciaReport />} />
          <Route path="struttura/:organizationId" element={<PanoramicaStruttura />} />
          <Route path="richieste" element={<RichiesteCarepy />} />
          <Route path="controllo-ricette" element={<ControlloRicette />} />
          <Route path="scorte" element={<ScorteFarmacia />} />
          <Route path="consegne" element={<ConsegneFarmacia />} />
          <Route path="autista" element={<AutistaConsegneFarmacia />} />
          <Route path="fatturazione" element={<FatturazioneFarmacia />} />
          <Route path="clienti" element={<ClientiDiretti />} />
          <Route path="catalogo" element={<CatalogoFarmacia />} />
          <Route path="impostazioni" element={<ImpostazioniFarmacia />} />
        </Route>
      </Route>
      <Route element={<ImmobiliPortalRoot />}>
        <Route path="/portale-immobiliare/login" element={<ImmobiliPortalLogin />} />
        <Route path="/portale-immobiliare" element={<ImmobiliPortal />} />
      </Route>
      <Route
        path="/centro-diurno"
        element={
          <ProtectedRoute requiredPermission="centro_diurno:read">
            <Layout>
              <CentroDiurnoDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centro-diurno/agenda"
        element={
          <ProtectedRoute requiredPermission="centro_diurno:read">
            <Layout>
              <CentroDiurnoAgenda />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centro-diurno/presenze"
        element={
          <ProtectedRoute requiredPermission="centro_diurno:read">
            <Layout>
              <CentroDiurnoPresenze />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centro-diurno/trasporti"
        element={
          <ProtectedRoute requiredPermission="centro_diurno:read">
            <Layout>
              <CentroDiurnoTrasporti />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centro-diurno/mezzi"
        element={
          <ProtectedRoute requiredPermission="centro_diurno:read">
            <Layout>
              <CentroDiurnoMezzi />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centro-diurno/pacchetti"
        element={
          <ProtectedRoute requiredPermission="centro_diurno:read">
            <Layout>
              <CentroDiurnoPacchetti />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centro-diurno/report"
        element={
          <ProtectedRoute requiredPermission="centro_diurno:read">
            <Layout>
              <CentroDiurnoReport />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/novita"
        element={
          <ProtectedRoute>
            <Layout>
              <NovitaAggiornamenti />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/alloggiati-web"
        element={
          <ProtectedRoute requiredPermission="fascicolo:read">
            <Layout>
              <AlloggiatiWeb />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/posti-liberi"
        element={
          <ProtectedRoute requiredPermission="struttura:read">
            <Layout>
              <PostiLiberi />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manutenzioni"
        element={
          <ProtectedRoute>
            <Layout>
              <Manutenzioni />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ospiti-in-rete"
        element={
          <ProtectedRoute>
            <Layout>
              <OspitiInRete />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trova-ospiti"
        element={
          <ProtectedRoute>
            <Layout>
              <TrovaOspiti />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trova-personale"
        element={
          <ProtectedRoute>
            <Layout>
              <TrovaPersonale />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/immobiliare"
        element={
          <ProtectedRoute>
            <Layout>
              <Immobiliare />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/personale"
        element={
          <ProtectedRoute requiredPermission="users:read">
            <Layout>
              <Personale />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/risparmio"
        element={
          <ProtectedRoute>
            <Layout>
              <Risparmio />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profilo-struttura"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfiloStruttura />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fascicoli-assistenziali"
        element={
          <ProtectedRoute requiredPermission="fascicolo:read">
            <Layout>
              <FascicoliAssistenziali />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fascicoli-assistenziali/:id"
        element={
          <ProtectedRoute requiredPermission="fascicolo:read">
            <Layout>
              <FascicoloAssistenzialeDettaglio />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fascicoli-assistenziali/:id/firma-contratto"
        element={
          <ProtectedRoute requiredPermission="fascicolo:read">
            <FirmaContratto />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deblistering/ospiti/:fascicolo_id/firma-delega"
        element={
          <ProtectedRoute requiredPermission="fascicolo:read">
            <FirmaDelegaMedico />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fatturazione"
        element={
          <ProtectedRoute requiredPermission="billing:read">
            <Layout>
              <Fatturazione />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fatturazione/nuova"
        element={
          <ProtectedRoute requiredPermission="billing:read">
            <NuovaFattura />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fatturazione/modelli"
        element={
          <ProtectedRoute requiredPermission="billing:read">
            <Layout>
              <ModelliFattura />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fatturazione/aspetto"
        element={
          <ProtectedRoute requiredPermission="billing:read">
            <Layout>
              <AspettoFattura />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsletter"
        element={
          <ProtectedRoute requiredPermission="admin:read">
            <Layout>
              <Newsletter />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/diario-personale"
        element={
          <ProtectedRoute requiredPermission="diario_personale:read">
            <Layout>
              <DiarioPersonale />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/diario-consegne"
        element={
          <ProtectedRoute requiredPermission="diario:read">
            <Layout>
              <DiarioConsegne />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documenti"
        element={
          <ProtectedRoute requiredPermission="documento:read">
            <Layout>
              <Documenti />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/comunicazioni"
        element={
          <ProtectedRoute requiredPermission="comunicazione:read">
            <Layout>
              <Comunicazioni />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/modulistica"
        element={
          <ProtectedRoute>
            <Navigate to="/documenti" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prima-nota-ospiti"
        element={
          <ProtectedRoute requiredPermission="billing:read">
            <Layout>
              <PrimaNotaOspiti />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/deblistering"
        element={
          <ProtectedRoute>
            <Layout>
              <Deblistering />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/arredo"
        element={
          <ProtectedRoute>
            <Layout>
              <Arredo />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/impostazioni"
        element={
          <ProtectedRoute>
            <Layout>
              <Impostazioni />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assistenza"
        element={
          <ProtectedRoute>
            <Layout>
              <Assistenza />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/family-struttura"
        element={
          <ProtectedRoute requiredPermission="fascicolo:read" requireAccess={canAccessFamily}>
            <Layout>
              <FamilyStruttura />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminPanel />
        }
      />
      {/* Admin Demo - Layout separato */}
      <Route path="/admin-demo" element={<AdminDemo />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Collega il tema al percorso corrente (le pagine pubbliche restano sempre chiare).
function RoutedThemeProvider({ children }) {
  const { pathname } = useLocation();
  return <ThemeProvider pathname={pathname}>{children}</ThemeProvider>;
}

// Toast di sonner: segue il tema effettivo (in light resta il default "light" di prima).
function ThemedToaster() {
  const { effectiveTheme } = useAppTheme();
  return <Toaster position="top-right" richColors theme={effectiveTheme} />;
}

function App() {
  // Avviso quando c'è un aggiornamento della PWA (webapp installata): "Ricarica" applica subito le modifiche
  useEffect(() => {
    const handler = (e) => {
      const { newWorker } = e.detail || {};
      if (!newWorker) return;
      toast.info("Nuova versione disponibile. Ricarica per aggiornare.", {
        action: {
          label: "Ricarica",
          onClick: () => newWorker.postMessage({ type: "SKIP_WAITING" }),
        },
        duration: Infinity,
      });
    };
    window.addEventListener("sw-update", handler);
    return () => window.removeEventListener("sw-update", handler);
  }, []);

  return (
    <BrowserRouter>
      <RoutedThemeProvider>
        <AuthProvider>
          <ThemedToaster />
          <AppRoutes />
          <PWAInstallPrompt />
        </AuthProvider>
      </RoutedThemeProvider>
    </BrowserRouter>
  );
}

export default App;
